import type { ColorSystemOptions } from "@mui/material/styles";

import { chartSeriesVar } from "../chartSeries";

/** The dark page's own ground, which the nav's washes are mixed over. */
const NAV_GROUND = "#181B1C";
/** A card lifted off that ground. */
const CARD_GROUND = "#24282B";

export const darkUiTokens: ColorSystemOptions["ui"] = {
  accent: {
    // The logo's own green (see `logo.gradient`) — the palette's dark ramp
    // puts its light greens at the TOP, so this is 600 and not `main`.
    //
    // NOT the chart ramp. This token dresses CHROME — the primary button's
    // fill, a switch's track — and pointing it at `chartSeriesVar(0)` turned
    // every Save button into the chart's leading colour. Charts read the ramp
    // directly; this stays the palette's accent.
    data: "var(--mui-palette-primary-600)"
  },
  button: {
    primary: "var(--mui-palette-primary-main)",
    primaryDeep: "var(--mui-palette-primary-main)"
  },
  card: {
    background: "rgba(31, 34, 42, 0.74)",
    border: "rgba(255, 255, 255, 0.12)",
    highlight: "rgba(255, 255, 255, 0.18)",
    shadow: "rgba(0, 0, 0, 0.42)"
  },
  dashboardCard: {
    background: "rgba(21, 24, 21, 0.62)",
    border: {
      from: "rgba(213, 215, 218, 0.2)",
      to: "rgba(255, 255, 255, 0.05)"
    },
    bottomOverlay: "rgba(255, 255, 255, 0)",
    hoverBackground: "rgba(21, 24, 21, 0.95)",
    topOverlay: "rgba(255, 255, 255, 0.055)"
  },
  drawer: {
    background: "rgba(22, 24, 28, 0.95)",
    border: {
      from: "rgba(255, 255, 255, 0.22)",
      mid: "rgba(255, 255, 255, 0.08)",
      to: "rgba(255, 255, 255, 0.03)"
    },
    bottomShade: "rgba(0, 0, 0, 0.16)",
    fallbackBackground: "#2A2E3B",
    shadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
    topHighlight: "rgba(255, 255, 255, 0.07)"
  },
  glassInteractive: {
    default: {
      background: "rgba(24, 26, 31, 0.56)",
      border: {
        from: "rgba(255, 255, 255, 0.28)",
        mid: "rgba(255, 255, 255, 0.10)",
        to: "rgba(255, 255, 255, 0.03)"
      },
      bottomOverlay: "rgba(0, 0, 0, 0.12)",
      shadow: "0 6px 16px rgba(0, 0, 0, 0.14)",
      topOverlay: "rgba(255, 255, 255, 0.08)"
    },
    emphasized: {
      background: "rgba(24, 26, 31, 0.78)",
      border: {
        from: "rgba(255, 255, 255, 0.78)",
        mid: "rgba(255, 255, 255, 0.24)",
        to: "rgba(255, 255, 255, 0.06)"
      },
      bottomOverlay: "rgba(0, 0, 0, 0.20)",
      shadow: "0 10px 24px rgba(0, 0, 0, 0.22)",
      topOverlay: "rgba(255, 255, 255, 0.16)"
    }
  },
  // The band is the deep green end of the palette here, sweeping to a lighter
  // step at the bottom-right; its ring is the palette too, not a neutral.
  // The same blend mixed over the dark ground: a lift, not a wash, so the
  // peak reads as the theme's colour caught in low light rather than a lighter
  // panel. Slot 1 here is the dark scheme's own mid tone already.
  hero: {
    background: {
      base: `color-mix(in srgb, ${chartSeriesVar(0)} 4%, ${NAV_GROUND})`,
      peak: `color-mix(in srgb, ${chartSeriesVar(0)} 26%, ${NAV_GROUND})`,
      tint: `color-mix(in srgb, ${chartSeriesVar(0)} 8%, ${NAV_GROUND})`
    },
    border:
      "color-mix(in srgb, var(--mui-palette-primary-400) 45%, transparent)",
    pill: "rgba(255, 255, 255, 0.08)",
    text: "var(--mui-palette-common-white)"
  },
  logo: {
    gradient: {
      from: "var(--mui-palette-primary-700)",
      to: "var(--mui-palette-primary-600)"
    },
    // 600, not `main`: the dark ramp is inverted, so `main` here is the
    // near-black the page is built on. This is the palette's readable step —
    // Desert's #EBB8A6, Forest's #AFEFC7.
    mark: "var(--mui-palette-primary-600)"
  },
  map: {
    land: "rgba(58, 58, 218, 0.32)",
    rippleInner: "rgba(255, 255, 255, 0.1)",
    rippleOutter: "rgba(0, 0, 0, 0.08)"
  },
  // The same derivation as the light scheme, mixed over the dark ground
  // instead of white, and reaching for the palette's LIGHT step (600, the
  // logo's own green) where light mode reaches for `main`.
  sidebar: {
    caption: "rgba(var(--mui-palette-text-primaryChannel) / 0.5)",
    card: {
      background: CARD_GROUND,
      hover: `color-mix(in srgb, var(--mui-palette-primary-600) 10%, ${CARD_GROUND})`
    },
    item: {
      icon: "rgba(var(--mui-palette-text-primaryChannel) / 0.5)",
      // The palette's readable step — the same value `logo.mark` resolves to,
      // so the highlight and the mark above it are one colour. NOT the chart
      // ramp: the nav is chrome, and pointing it there made the highlight a
      // brighter blue than the logo it sat under.
      selected: {
        background:
          "color-mix(in srgb, var(--mui-palette-primary-600) 14%, transparent)",
        ring: "color-mix(in srgb, var(--mui-palette-primary-600) 25%, transparent)",
        text: "var(--mui-palette-primary-600)"
      },
      text: "rgba(var(--mui-palette-text-primaryChannel) / 0.82)"
    },
    line: "rgba(255, 255, 255, 0.12)",
    nav: {
      background: `color-mix(in srgb, var(--mui-palette-primary-600) 5%, ${NAV_GROUND})`,
      group: `color-mix(in srgb, var(--mui-palette-primary-600) 7.5%, ${NAV_GROUND})`,
      hover: `color-mix(in srgb, var(--mui-palette-primary-600) 10%, ${NAV_GROUND})`,
      line: `color-mix(in srgb, var(--mui-palette-primary-600) 14%, ${NAV_GROUND})`
    }
  },
  table: {
    bodyRowHover: "#181C1C",
    bodyRowShadow: "#292C3A",
    border: "#3A404C",
    cellError: "rgb(100, 65, 75)",
    cellGreen: "rgb(65, 100, 80)",
    cellRed: "rgb(100, 65, 75)",
    headerRowShadow: "#474F5B"
  },
  topBar: {
    background: "var(--mui-palette-background-default)",
    crumbScope: "rgba(var(--mui-palette-text-primaryChannel) / 0.55)",
    crumbSection: "var(--mui-palette-text-primary)",
    hover: "rgba(var(--mui-palette-text-primaryChannel) / 0.08)",
    line: "rgba(255, 255, 255, 0.12)",
    lineHover: "rgba(255, 255, 255, 0.28)",
    separator: "rgba(var(--mui-palette-text-primaryChannel) / 0.35)"
  }
};
